import React, { useState } from 'react';
import { Linking, Platform, View, useWindowDimensions } from 'react-native';

import { useT } from '@transifex/react';

import { AuthFooter } from '../components/AuthFooter';
import { AuthLayout } from '../components/AuthLayout';
import { BloomButton } from '../components/BloomButton';
import { InlineError } from '../components/InlineError';
import { Input } from '../components/Input';
import { Logo } from '../components/Logo';
import ThemedText from '../components/ThemedText';
import { ROUTES, EXTERNAL_URLS, ERROR_MESSAGES } from '../utils/constants';
import { screenTestId } from '../utils/testId';
import { validateEmail } from '../services/validationService';
import { sendPasswordResetEmail } from '../api/auth';
import { analytics } from '../utils/analytics';
import { useLoginRouter } from '../hooks/useLoginRouter';

const tid = screenTestId('ForgotPassword');

export default function ForgotPasswordScreen() {
  const router = useLoginRouter();
  const { width } = useWindowDimensions();
  const t = useT();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const isDesktop = width >= 768;

  const handleSubmit = async () => {
    setEmailError(null);
    setError(null);
    setSuccess(false);

    const emailValidation = validateEmail(email);
    if (emailValidation) {
      setEmailError(emailValidation);
      return;
    }

    setLoading(true);
    analytics.trackEvent('forgot_password_submitted');

    try {
      await sendPasswordResetEmail({ email });
      analytics.trackEvent('forgot_password_succeeded');
      setSuccess(true);
    } catch (err: any) {
      analytics.trackEvent('forgot_password_failed');
      setError(ERROR_MESSAGES.PASSWORD_RESET_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    router.replace(ROUTES.LOGIN);
  };

  return (
    <AuthLayout>
      <View className='w-full'>
        <Logo />

        <View className='w-full'>
          <ThemedText type='titleHero' className='mb-2 font-bold' style={{ fontSize: 32, lineHeight: 40 }}>
            {t('Forgot Password')}
          </ThemedText>
          <ThemedText
            type='bodyRegular'
            color='subtle'
            className={isDesktop ? 'mb-6' : 'mb-8'}
          >
            {t("Enter your email address and we'll send you a link to reset your password.")}
          </ThemedText>

          <InlineError message={error} testID={tid('error')} />

          {success && (
            <View className='bg-login-green-10 rounded-lg p-3 px-9 mt-2 mb-4' testID={tid('success-message')}>
              <ThemedText type='bodySmall' className='text-login-green-90 text-center'>
                {ERROR_MESSAGES.PASSWORD_RESET_SUCCESS}
              </ThemedText>
            </View>
          )}

          <Input
            label={t('Email')}
            value={email}
            onChangeText={setEmail}
            placeholder={t('Enter your email address')}
            keyboardType='email-address'
            autoComplete='email'
            autoCorrect={false}
            spellCheck={false}
            maxLength={254}
            error={emailError || undefined}
            onSubmitEditing={handleSubmit}
            testID={tid('email-input')}
          />

          <View>
            <BloomButton text={t('Reset Password')} onPress={handleSubmit} loading={loading} testID={tid('reset-password-button')} />
          </View>

          <View className='mt-4'>
            <BloomButton
              text={t('Forgot Email?')}
              href={EXTERNAL_URLS.SUPPORT_EMAIL}
              onPress={() => {
                if (Platform.OS !== 'web') {
                  Linking.openURL(EXTERNAL_URLS.SUPPORT_EMAIL);
                }
              }}
              hierarchy='secondary'
              testID={tid('forgot-email-button')}
            />
          </View>

          <AuthFooter
            variant='secondary'
            onBackToLogin={handleBack}
            testID={tid('footer')}
          />
        </View>
      </View>
    </AuthLayout>
  );
}
